import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import api from '../../../services/api';
import { NINE_BOX_CELL_KEYS } from '../config';
import { buildEmployeeAsync } from '../asyncHelpers';
import { useAuth } from '../../../context/AuthContext';
import { parseTableParams, formatDate } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput.jsx';
import GenericSelect from '../../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import { readPage } from '../../../utils/apiResult';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 560px;
  width: 100%;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
`;

const CardTitle = styled.h3`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const NoteItem = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
`;

const NoteMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 4px;
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const employeeAsync = buildEmployeeAsync();

const EMPTY_ADD_FORM = {
  employeeId: null,
  label: '',
  targetPosition: '',
  developmentPriority: '',
  investmentLevel: 'standard',
  riskStatus: 'low',
  entryReason: '',
};

export default function PoolMembersTab({ poolId, isDynamic }) {
  const { t, i18n } = useTranslation();
  // İK rolü ayrımı — sunucu (TalentPoolController) bu görünümü IsHr ile açar; aynı karar.
  const { canWrite, isHr } = useAuth();
  const isAdmin = Boolean(isHr);
  const canManage = canWrite('TD');

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [saving, setSaving] = useState(false);

  const [removeModal, setRemoveModal] = useState({ open: false, row: null });
  const [removeReason, setRemoveReason] = useState('');
  const [removing, setRemoving] = useState(false);

  const [notesModal, setNotesModal] = useState({ open: false, row: null });
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [flightRiskModal, setFlightRiskModal] = useState({ open: false, row: null });
  const [flightRiskLevel, setFlightRiskLevel] = useState('low');
  const [savingFlightRisk, setSavingFlightRisk] = useState(false);

  const [tagsModal, setTagsModal] = useState({ open: false, row: null });
  const [savingTag, setSavingTag] = useState(null);

  // TD-23 (kalan yüzeyler, Faz 2) — bkz. utils/apiResult.js.
  const [loadFailed, setLoadFailed] = useState(false);
  const [notesLoadFailed, setNotesLoadFailed] = useState(false);
  const [filterOptionsFailed, setFilterOptionsFailed] = useState(false);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // TD-23 (kalan yüzeyler, Faz 2) — FİLTRE SEÇENEKLERİ de sessizce boşalıyordu:
  // departman/lokasyon/etiket listesi yüklenemediğinde seçiciler boş açılıyor ve
  // kullanıcı "bu havuzda hiç departman yok" sanıyordu. Üçü tek bayrağı besler —
  // hangisinin düştüğü kullanıcı için ayırt edici değil, sonuç aynı: eksik filtre.
  useEffect(() => {
    let auxFailed = false;
    const mark = (ok) => { if (!ok) auxFailed = true; };
    Promise.all([
      api.get('/v1/departments', { params: { pageSize: 500 } }).then((res) => {
        const { ok, items } = readPage(res);
        mark(ok);
        setDepartments(items);
      }),
      api.get('/v1/locations', { params: { pageSize: 500 } }).then((res) => {
        const { ok, items } = readPage(res);
        mark(ok);
        setLocations(items);
      }),
      api.get('/v1/talent-pool/tags').then((res) => {
        const { ok, items } = readPage(res);
        mark(ok);
        setTags(items);
      }),
    ]).then(() => setFilterOptionsFailed(auxFailed));
  }, []);

  useEffect(() => {
    if (!poolId) return;
    setFetching(true);
    api.get(`/v1/talent-pool/pools/${poolId}/members?${new URLSearchParams(queryParams)}`)
      .then((res) => {
        const { ok, items, pagination: page } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(page);
      })
      .finally(() => setFetching(false));
  }, [poolId, queryParams, refreshTrigger]);

  const cellKeyOptions = useMemo(() => NINE_BOX_CELL_KEYS.map((k) => ({
    id: k, name: t(`performance.ninebox.cell.${k}`, { defaultValue: k }),
  })), [t]);

  const riskStatusOptions = useMemo(() => [
    { id: 'low', name: t('performance.talentpool.risk.low') },
    { id: 'medium', name: t('performance.talentpool.risk.medium') },
    { id: 'high', name: t('performance.talentpool.risk.high') },
  ], [t]);

  const investmentLevelOptions = useMemo(() => [
    { id: 'high', name: t('performance.talentpool.investment.high') },
    { id: 'medium', name: t('performance.talentpool.investment.medium') },
    { id: 'standard', name: t('performance.talentpool.investment.standard') },
  ], [t]);

  const flightRiskOptions = useMemo(() => [
    { id: 'low', name: t('performance.talentpool.flightRisk.low') },
    { id: 'medium', name: t('performance.talentpool.flightRisk.medium') },
    { id: 'high', name: t('performance.talentpool.flightRisk.high') },
  ], [t]);

  // ---- Add / Remove member (static pool only) ----

  const handleOpenAdd = useCallback(() => {
    setAddForm(EMPTY_ADD_FORM);
    setAddModalOpen(true);
  }, []);

  const handleSaveAdd = useCallback(async () => {
    setSaving(true);
    try {
      await api.post('/v1/talent-pool', {
        poolId,
        employeeId: addForm.employeeId,
        label: addForm.label,
        targetPosition: addForm.targetPosition || undefined,
        developmentPriority: addForm.developmentPriority || undefined,
        investmentLevel: addForm.investmentLevel,
        riskStatus: addForm.riskStatus,
        entryReason: addForm.entryReason,
      });
      setAddModalOpen(false);
      refresh();
    } catch {
      // handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [poolId, addForm, refresh]);

  const handleOpenRemove = useCallback((row) => {
    setRemoveModal({ open: true, row });
    setRemoveReason('');
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeReason.trim()) return;
    setRemoving(true);
    try {
      await api.post(`/v1/talent-pool/${removeModal.row.id}/remove`, { reason: removeReason });
      setRemoveModal({ open: false, row: null });
      refresh();
    } catch {
      // handled by interceptor
    } finally {
      setRemoving(false);
    }
  }, [removeModal.row, removeReason, refresh]);

  // ---- Notes ----

  const handleOpenNotes = useCallback(async (row) => {
    setNotesModal({ open: true, row });
    setNoteText('');
    setNotesLoading(true);
    try {
      // TD-23 — "not yok" ile "notlar yüklenemedi" AYRI: yönetici boş bir not
      // paneline bakıp kişi hakkında hiç değerlendirme yazılmadığı sonucuna
      // varıyordu. (`catch` dalı ölü koddur — bkz. utils/apiResult.js.)
      const res = await api.get(`/v1/talent-pool/pools/${poolId}/employees/${row.employeeId}/notes`);
      const { ok, items } = readPage(res);
      setNotesLoadFailed(!ok);
      setNotes(items);
    } finally {
      setNotesLoading(false);
    }
  }, [poolId]);

  const handleAddNote = useCallback(async () => {
    if (!noteText.trim() || !notesModal.row) return;
    setSavingNote(true);
    try {
      await api.post(`/v1/talent-pool/pools/${poolId}/employees/${notesModal.row.employeeId}/notes`, { text: noteText });
      const res = await api.get(`/v1/talent-pool/pools/${poolId}/employees/${notesModal.row.employeeId}/notes`);
      const { ok, items } = readPage(res);
      setNotesLoadFailed(!ok);
      setNotes(items);
      setNoteText('');
    } catch {
      // handled by interceptor
    } finally {
      setSavingNote(false);
    }
  }, [poolId, notesModal.row, noteText]);

  // ---- Flight risk (İK) ----

  const handleOpenFlightRisk = useCallback((row) => {
    setFlightRiskModal({ open: true, row });
    setFlightRiskLevel(row.flightRiskLevel || 'low');
  }, []);

  const handleSaveFlightRisk = useCallback(async () => {
    if (!flightRiskModal.row) return;
    setSavingFlightRisk(true);
    try {
      await api.post(`/v1/talent-pool/employees/${flightRiskModal.row.employeeId}/flight-risk`, { level: flightRiskLevel });
      setFlightRiskModal({ open: false, row: null });
      refresh();
    } catch {
      // handled by interceptor
    } finally {
      setSavingFlightRisk(false);
    }
  }, [flightRiskModal.row, flightRiskLevel, refresh]);

  // ---- Tags ----

  const handleOpenTags = useCallback((row) => {
    setTagsModal({ open: true, row });
  }, []);

  const assignedTagIds = useMemo(() => {
    if (!tagsModal.row) return new Set();
    const rowTagNames = new Set(tagsModal.row.tags || []);
    return new Set(tags.filter((tg) => rowTagNames.has(tg.name)).map((tg) => tg.id));
  }, [tagsModal.row, tags]);

  const handleToggleTag = useCallback(async (tag, assigned) => {
    if (!tagsModal.row) return;
    setSavingTag(tag.id);
    try {
      if (assigned) {
        await api.post(`/v1/talent-pool/employees/${tagsModal.row.employeeId}/tags/${tag.id}/remove`);
      } else {
        await api.post(`/v1/talent-pool/employees/${tagsModal.row.employeeId}/tags`, { tagId: tag.id });
      }
      // TD-23 — etiket yazımından SONRAKİ tazeleme okuması da başarısız olabilir;
      // eskiden liste sessizce boşalıyor ve kullanıcı etiketlemenin üyeyi havuzdan
      // düşürdüğünü sanıyordu. Başarısızlıkta mevcut liste KORUNUR (boşaltılmaz).
      const memberRes = await api.get(`/v1/talent-pool/pools/${poolId}/members?${new URLSearchParams(queryParams)}`);
      const { ok, items } = readPage(memberRes);
      setLoadFailed(!ok);
      if (!ok) return;
      setData(items);
      const updatedRow = items.find((m) => m.employeeId === tagsModal.row.employeeId);
      if (updatedRow) setTagsModal((m) => ({ ...m, row: updatedRow }));
    } catch {
      // handled by interceptor
    } finally {
      setSavingTag(null);
    }
  }, [tagsModal.row, poolId, queryParams]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useCallback((row) => {
    const opts = [
      { type: 'notes', label: t('performance.talentpool.tabNotes'), onClick: () => handleOpenNotes(row) },
    ];
    if (canManage) {
      opts.push({ type: 'tags', label: t('performance.talentpool.manageTags'), onClick: () => handleOpenTags(row) });
    }
    // Spec §6.3: "uçuş riski... İK manuel işaretler" — backend SetFlightRiskAsync herhangi
    // bir TD.Write sahibini kabul eder (isAdmin/enterpriseAdmin ModuleAuthorize'ı zaten
    // bypass eder), ama FE burada BİLİNÇLİ OLARAK yalnız İK'ya (isAdmin) gösterir; sıradan
    // yöneticiler işaretleyemez.
    if (isAdmin) {
      opts.push({ type: 'flightRisk', label: t('performance.talentpool.setFlightRisk'), onClick: () => handleOpenFlightRisk(row) });
    }
    if (canManage && !isDynamic) {
      opts.push({ type: 'divider' });
      opts.push({ type: 'remove', label: t('performance.talentpool.remove'), onClick: () => handleOpenRemove(row) });
    }
    return opts;
  }, [t, canManage, isAdmin, isDynamic, handleOpenNotes, handleOpenTags, handleOpenFlightRisk, handleOpenRemove]);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.talentpool.member'),
      options: { filter: false, sort: false },
    },
    {
      name: 'label',
      label: t('performance.talentpool.label'),
      options: { filter: false, sort: false },
    },
    {
      name: 'riskStatus',
      label: t('performance.talentpool.riskStatus'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          const opt = riskStatusOptions.find((o) => o.id === v);
          return opt ? opt.name : (v || '-');
        },
      },
    },
    {
      name: 'nineBoxCell',
      label: t('performance.talentpool.nineBoxCell'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? t(`performance.ninebox.cell.${v}`, { defaultValue: v }) : '-'),
      },
    },
    {
      name: 'tags',
      label: t('performance.talentpool.tags'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (Array.isArray(v) && v.length > 0 ? v.join(', ') : '-'),
      },
    },
    {
      name: 'flightRiskLevel',
      label: t('performance.talentpool.flightRisk.label'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          if (!v) return '-';
          const opt = flightRiskOptions.find((o) => o.id === v);
          return opt ? opt.name : v;
        },
      },
    },
    // ---- Filtre-yalnız (görünmez) kolonlar — GetPoolMembersAsync filtreleri ----
    {
      name: 'departmentId',
      label: t('department.title'),
      options: {
        filter: true, sort: false, display: 'excluded', filterType: 'custom',
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('department.title')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {departments.map((d) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'cellKey',
      label: t('performance.talentpool.nineBoxCell'),
      options: {
        filter: true, sort: false, display: 'excluded', filterType: 'custom',
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.talentpool.nineBoxCell')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {cellKeyOptions.map((o) => (<MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'tagId',
      label: t('performance.talentpool.tags'),
      options: {
        filter: true, sort: false, display: 'excluded', filterType: 'custom',
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.talentpool.tags')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {tags.map((tg) => (<MenuItem key={tg.id} value={tg.id}>{tg.name}</MenuItem>))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'seniorityYears',
      label: t('performance.talentpool.seniorityYearsGte'),
      options: {
        filter: true, sort: false, display: 'excluded',
        filterType: 'textField',
        filterOptions: { defaultOp: 'gte' },
      },
    },
    {
      name: 'locationId',
      label: t('location.title'),
      options: {
        filter: true, sort: false, display: 'excluded', filterType: 'custom',
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('location.title')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {locations.map((l) => (<MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
  ], [t, riskStatusOptions, flightRiskOptions, cellKeyOptions, departments, locations, tags]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={actionOptions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, data, actionOptions]);

  const canSaveAdd = addForm.employeeId && addForm.label.trim() && addForm.entryReason.trim() && addForm.riskStatus;

  return (
    <div>
      {/* TD-23 — üye listesi ve filtre seçenekleri AYRI bantlar: biri
          yüklenmediğinde diğerinin durumu hakkında yanlış izlenim verilmemeli. */}
      {loadFailed && <LoadErrorBanner />}
      {filterOptionsFailed && <LoadErrorBanner message={t('talent.filterOptionsLoadFailed')} />}
      {/* Add member modal (static pool only) */}
      <Overlay $open={addModalOpen}>
        {addModalOpen && (
          <Card>
            <CardTitle>{t('performance.talentpool.add')}</CardTitle>
            <FieldGroup>
              <AsyncGenericSelect
                label={t('performance.talentpool.member')}
                value={addForm.employeeId}
                onChange={(v) => setAddForm((f) => ({ ...f, employeeId: v }))}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.talentpool.label')}
                value={addForm.label}
                onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.talentpool.targetPosition')}
                value={addForm.targetPosition}
                onChange={(e) => setAddForm((f) => ({ ...f, targetPosition: e.target.value }))}
              />
            </FieldGroup>
            <FieldGroup>
              <GenericSelect
                label={t('performance.talentpool.investmentLevel')}
                value={addForm.investmentLevel}
                onChange={(v) => setAddForm((f) => ({ ...f, investmentLevel: v }))}
                options={investmentLevelOptions}
              />
            </FieldGroup>
            <FieldGroup>
              <GenericSelect
                label={t('performance.talentpool.riskStatus')}
                value={addForm.riskStatus}
                onChange={(v) => setAddForm((f) => ({ ...f, riskStatus: v }))}
                options={riskStatusOptions}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.talentpool.entryReason')}
                type="textarea"
                rows={3}
                value={addForm.entryReason}
                onChange={(e) => setAddForm((f) => ({ ...f, entryReason: e.target.value }))}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setAddModalOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveAdd} disabled={!canSaveAdd || saving} loading={saving}>{t('common.save')}</Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      {/* Remove member modal */}
      <Overlay $open={removeModal.open}>
        {removeModal.open && (
          <Card>
            <CardTitle>{t('performance.talentpool.remove')}</CardTitle>
            <FieldGroup>
              <TextInput
                label={t('performance.talentpool.removeReason')}
                type="textarea"
                rows={3}
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setRemoveModal({ open: false, row: null })}>{t('common.cancel')}</Button>
              <Button onClick={handleRemoveConfirm} disabled={!removeReason.trim() || removing} loading={removing}>
                {t('common.confirm')}
              </Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      {/* Notes modal */}
      <Overlay $open={notesModal.open}>
        {notesModal.open && (
          <Card>
            <CardTitle>{t('performance.talentpool.tabNotes')} — {notesModal.row?.employeeName}</CardTitle>
            {/* TD-23 — "not yok" boş-durumu YALNIZ okuma başarılıysa gösterilir;
                aksi halde yönetici, yüklenemeyen notlara bakıp kişi hakkında hiç
                değerlendirme yazılmadığı sonucuna varıyordu. */}
            {notesLoading ? (
              <div>{t('common.loading')}...</div>
            ) : notesLoadFailed ? (
              <LoadErrorBanner />
            ) : notes.length === 0 ? (
              <div style={{ fontSize: 13, color: '#6B7280' }}>{t('performance.talentpool.noNotes')}</div>
            ) : (
              notes.map((n) => (
                <NoteItem key={n.id}>
                  {n.text}
                  <NoteMeta>{formatDate(n.createDate, i18n.language)}</NoteMeta>
                </NoteItem>
              ))
            )}
            {canManage && (
              <FieldGroup style={{ marginTop: 16 }}>
                <TextInput
                  label={t('performance.talentpool.newNote')}
                  type="textarea"
                  rows={3}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
              </FieldGroup>
            )}
            <Actions>
              <Button variant="outline" onClick={() => setNotesModal({ open: false, row: null })}>{t('common.close')}</Button>
              {canManage && (
                <Button onClick={handleAddNote} disabled={!noteText.trim() || savingNote} loading={savingNote}>
                  {t('common.add')}
                </Button>
              )}
            </Actions>
          </Card>
        )}
      </Overlay>

      {/* Flight risk modal (İK) */}
      <Overlay $open={flightRiskModal.open}>
        {flightRiskModal.open && (
          <Card>
            <CardTitle>{t('performance.talentpool.setFlightRisk')} — {flightRiskModal.row?.employeeName}</CardTitle>
            <FieldGroup>
              <GenericSelect
                label={t('performance.talentpool.flightRisk.label')}
                value={flightRiskLevel}
                onChange={setFlightRiskLevel}
                options={flightRiskOptions}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setFlightRiskModal({ open: false, row: null })}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveFlightRisk} disabled={savingFlightRisk} loading={savingFlightRisk}>{t('common.save')}</Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      {/* Tags modal */}
      <Overlay $open={tagsModal.open}>
        {tagsModal.open && (
          <Card>
            <CardTitle>{t('performance.talentpool.manageTags')} — {tagsModal.row?.employeeName}</CardTitle>
            {tags.length === 0 ? (
              <div style={{ fontSize: 13, color: '#6B7280' }}>{t('performance.talentpool.noTags')}</div>
            ) : (
              tags.map((tag) => {
                const assigned = assignedTagIds.has(tag.id);
                return (
                  <TagRow key={tag.id}>
                    <CheckboxInput
                      label={tag.name}
                      checked={assigned}
                      onChange={() => { if (!savingTag) handleToggleTag(tag, assigned); }}
                    />
                  </TagRow>
                );
              })
            )}
            <Actions>
              <Button variant="outline" onClick={() => setTagsModal({ open: false, row: null })}>{t('common.close')}</Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      <DataTable
        title={t('performance.talentpool.tabMembers')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canManage && !isDynamic}
        newButtonLabel={t('performance.talentpool.add')}
        onNewButtonClick={handleOpenAdd}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </div>
  );
}
